import { TaskLogsResponse, LogEntry, TaskStage } from '@/service/gpu-instance/type';

// Generate realistic server logs for GPU instances
const generateMockLogs = (taskId: string, count: number, startTimestamp?: string): LogEntry[] => {
  const logs: LogEntry[] = [];
  const now = Date.now();

  // Start from the given timestamp or current time
  let baseTime = startTimestamp
    ? BigInt(startTimestamp)
    : BigInt(now) * BigInt(1_000_000); // Convert ms to nanoseconds

  const logMessages = [
    '[INFO] Starting container initialization...',
    '[INFO] Loading CUDA drivers...',
    '[INFO] CUDA driver version: 12.2',
    '[INFO] Detected GPU: NVIDIA A100-SXM4-80GB',
    '[INFO] GPU memory: 81920 MiB',
    '[INFO] Initializing vLLM server...',
    '[INFO] Loading model weights from /models/llama-3.1-70b-instruct',
    '[INFO] Model loaded successfully',
    '[INFO] Warming up model with sample inference...',
    '[INFO] Server ready, listening on port 8000',
    '[DEBUG] Health check passed',
    '[INFO] Received inference request from 10.0.0.15',
    '[DEBUG] Input tokens: 128, Output tokens: 256',
    '[INFO] Request completed in 1.23s',
    '[DEBUG] GPU utilization: 78%',
    '[DEBUG] Memory usage: 65432 MiB / 81920 MiB',
    '[INFO] Batch processing enabled, batch size: 32',
    '[WARN] High memory usage detected, consider reducing batch size',
    '[INFO] Received inference request from 10.0.0.22',
    '[DEBUG] Input tokens: 512, Output tokens: 1024',
    '[INFO] Request completed in 3.45s',
    '[DEBUG] KV cache hit ratio: 0.85',
    '[INFO] Auto-scaling worker count to 4',
    '[DEBUG] Worker 0: idle, Worker 1: processing, Worker 2: idle, Worker 3: processing',
    '[INFO] Checkpoint saved to /storage/checkpoints/latest',
    '[DEBUG] Disk I/O: read 125 MB/s, write 89 MB/s',
    '[INFO] Connection from client 10.0.0.33 established',
    '[DEBUG] TLS handshake completed',
    '[INFO] Streaming response started',
    '[INFO] Streaming response completed, total tokens: 2048',
    '[DEBUG] Request latency p50: 1.2s, p99: 4.5s',
    '[INFO] Metrics exported to prometheus endpoint',
    '[DEBUG] Active connections: 12',
    '[INFO] Rate limit: 100 req/min, current: 45 req/min',
    '[WARN] Approaching rate limit threshold',
    '[INFO] Cache eviction triggered, freed 8192 MiB',
    '[DEBUG] New cache size: 32768 MiB',
    '[INFO] Model version check: up to date',
    '[DEBUG] Heartbeat sent to control plane',
    '[INFO] Received configuration update',
    '[DEBUG] Applying new configuration...',
    '[INFO] Configuration applied successfully',
    '[ERROR] Connection timeout to storage backend, retrying...',
    '[INFO] Storage connection restored',
    '[DEBUG] Syncing state with replica nodes',
    '[INFO] State sync completed',
    '[DEBUG] Garbage collection triggered',
    '[INFO] GC completed, reclaimed 4096 MiB',
  ];

  for (let i = 0; i < count; i++) {
    // Go back in time for each log entry (older logs have smaller timestamps)
    const offset = BigInt(i) * BigInt(5_000_000_000); // 5 seconds between each log
    const timestamp = (baseTime - offset).toString();
    const message = logMessages[i % logMessages.length];

    logs.push({
      timestamp,
      message,
    });
  }

  return logs;
};

// Generate mock stages data
const getMockStages = (): TaskStage[] => [
  {
    stage: 'docker_pull',
    status: 'completed',
    image: 'vllm/vllm-openai:v0.14.1',
    elapsed_seconds: 45.2,
  },
  {
    stage: 'hf_download',
    status: 'completed',
    model_id: 'Qwen/Qwen2.5-7B-Instruct',
    total_size_human: '15.2 GB',
    file_count: 12,
  },
  {
    stage: 'vllm_server',
    status: 'running',
    api_ready: true,
    served_model: 'Qwen/Qwen2.5-7B-Instruct',
  },
];

export const mockTaskLogs = (
  taskId: string,
  limit: number = 100,
  start?: string
): TaskLogsResponse => {
  const logs = generateMockLogs(taskId, limit, start);

  return {
    status: 'success',
    task_id: taskId,
    stages: getMockStages(),
    logs,
    total: 1000, // Indicate there are more logs available
  };
};
